/**
 * [PORTABLE] Self-contained "Selfie Pillar" UI.
 * Target path in main project: src/features/onboarding/components/SelfiePillar.tsx
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Trophy, Rocket, Sparkles } from "lucide-react";
import mixpanel from "mixpanel-browser";

import { Button } from "@/components/ui/button";
import useAuthStore from "@/store/use-auth-store";

import { selfieGridItems, SELFIE_LEVELS } from "../data/selfie-grid";
import { useOnboarding } from "../hooks/use-onboarding";
import { libraryItemUrl } from "../utils/media-url";

import { OnboardingSkeleton } from "./OnboardingSkeleton";
import { SelfieGrid, type SelfieGridCellState } from "./SelfieGrid";
import { SelfieLevelDialog } from "./SelfieLevelDialog";
import { SelfieUploadModal } from "./SelfieUploadModal";
import { BadgesStrip } from "./BadgesStrip";

const DEFAULT_TOTAL_CREDITS = 3000;

interface SelfiePillarProps {
  totalCreditsFallback?: number;
  cameraIconSrc?: string;
  footerSlot?: React.ReactNode;
}

export function SelfiePillar({
  totalCreditsFallback = DEFAULT_TOTAL_CREDITS,
  cameraIconSrc,
  footerSlot,
}: SelfiePillarProps) {
  const { t } = useTranslation();
  const { user, accessToken } = useAuthStore();
  const apiEnabled = !!accessToken;

  const {
    progress,
    isLoadingProgress,
    openPromptModal,
    openUploadModal,
    setSelectedSelfieLibraryItemId: setSelfieLibraryItemId,
    selfie: generateAndPoll,
    creditsData,
  } = useOnboarding();

  /* ---------- Veri Okuma ---------- */
  const selfieLevels = progress?.selfie?.levels ?? [];
  const uploadedLibraryItem = progress?.selfie?.uploadedLibraryItemId;
  const uploadedSelfieUrl = libraryItemUrl(uploadedLibraryItem);

  useEffect(() => {
    if (uploadedLibraryItem?._id) {
      setSelfieLibraryItemId(uploadedLibraryItem._id);
    }
  }, [uploadedLibraryItem?._id, setSelfieLibraryItemId]);

  const [resultByLevelId, setResultByLevelId] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (!selfieLevels.length) return;
    const next: Record<string, string | undefined> = {};
    for (const lvl of selfieLevels) {
      if (!lvl.levelId) continue;

      if (lvl.status === "failed") {
        next[lvl.levelId] = undefined;
        continue;
      }

      const url = libraryItemUrl(lvl.libraryItemId);
      if (url) next[lvl.levelId] = url;
    }
    setResultByLevelId((prev) => ({ ...prev, ...next }));
  }, [selfieLevels]);

  useEffect(() => {
    if (!generateAndPoll.isDone && !generateAndPoll.isFailed) return;

    generateAndPoll.onCompleted();
    generateAndPoll.reset();
  }, [generateAndPoll.isDone, generateAndPoll.isFailed, generateAndPoll]);

  /* ---------- Kilit & İlerleme Mantığı ---------- */
  const cellState = useMemo<Record<string, SelfieGridCellState>>(() => {
    const map: Record<string, SelfieGridCellState> = {};
    
    let previousLevelCompleted = !!uploadedSelfieUrl;
    let hasFailedStep = false; 

    for (const item of selfieGridItems) {
      const lvlData = selfieLevels.find((l) => l.levelId === item.levelId);
      const url = resultByLevelId[item.levelId];

      const isFailed = lvlData?.status === "failed";
      const isCompleted = !hasFailedStep && !isFailed && (lvlData?.isCompleted || lvlData?.status === "completed" || !!url);
      const unlocked = previousLevelCompleted && !hasFailedStep;

      map[item.levelId] = { 
        url: isFailed || hasFailedStep ? undefined : url, 
        completed: isCompleted, 
        unlocked 
      };

      if (isFailed) {
        hasFailedStep = true;
      }

      previousLevelCompleted = isCompleted;
    }

    return map;
  }, [selfieLevels, resultByLevelId, uploadedSelfieUrl]);

  /* ---------- Stats & Kredi ---------- */
  const completedCount = Math.min(
    SELFIE_LEVELS,
    selfieGridItems.filter((item) => cellState[item.levelId]?.completed).length
  );
  const progressPercentage = Math.round((completedCount / SELFIE_LEVELS) * 100);

  const creditsRemaining = creditsData?.usage?.tokens?.subscription ?? 0;
  const creditsTotal = creditsData?.usage?.maxTokens?.subscription ?? totalCreditsFallback;
  const isAllLevelsCompleted = completedCount >= SELFIE_LEVELS;

  const displayBadges = (progress?.badges ?? []).filter((badge) => {
    if (badge.type === "selfie_complete" && !isAllLevelsCompleted) {
      return false; 
    }
    return true; 
  });

  if (apiEnabled && isLoadingProgress) {
    return <OnboardingSkeleton count={selfieGridItems.length} columns={3} />;
  }

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-12">
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        
        {/* 1. Kart: Kredi */}
        <div className="glass-card p-6 rounded-2xl flex flex-col items-center justify-center text-center border-b-4 border-blue-500 shadow-sm transition-transform hover:scale-[1.02]">
          <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20">
            <Sparkles className="text-white h-8 w-8" />
          </div>
          <div className="text-text-secondary font-semibold text-xs uppercase tracking-wider mb-1">
            {t("onboarding.creditsLeft", { defaultValue: "Kalan Kredi" })}
          </div>
          <div className="font-extrabold text-3xl sm:text-4xl text-blue-500 mb-1">
            {creditsRemaining}
          </div>
          <div className="w-full bg-bg-tertiary rounded-full h-1.5 mt-2 overflow-hidden">
            <div className="bg-blue-500 h-full rounded-full" style={{ width: `${(creditsRemaining / creditsTotal) * 100}%` }} />
          </div>
          <div className="text-text-tertiary font-semibold text-[10px] mt-3 opacity-80 uppercase tracking-widest">
            {t("onboarding.totalCredits", { total: creditsTotal })}
          </div>
        </div>

        {/* 2. Kart: İlerleme (Oyunlaştırılmış Dairesel Bar) */}
        <div className="glass-card p-6 rounded-2xl flex flex-col items-center justify-center text-center border-b-4 border-pink-500 shadow-sm transition-transform hover:scale-[1.02]">
          <div className="relative w-24 h-24 mb-4 flex items-center justify-center group">
            <svg className="absolute inset-0 w-full h-full -rotate-90 drop-shadow-sm">
              <circle cx="48" cy="48" r="40" className="stroke-bg-tertiary opacity-40" strokeWidth="6" fill="transparent" />
              <circle
                cx="48" cy="48" r="40"
                className="stroke-pink-500 transition-all duration-1000 ease-in-out"
                strokeWidth="6" fill="transparent" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 40}
                strokeDashoffset={2 * Math.PI * 40 - (progressPercentage / 100) * (2 * Math.PI * 40)}
              />
            </svg>
            <Trophy 
              className={`relative z-10 h-8 w-8 transition-all duration-500 ${
                isAllLevelsCompleted ? "text-yellow-400 fill-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.6)] scale-110" : "text-pink-500"
              }`} 
            />
          </div>
          <div className="text-text-secondary font-semibold text-xs uppercase tracking-wider mb-1">
            {t("onboarding.currentStep", { defaultValue: "Mevcut Adım" })}
          </div>
          <div className="font-extrabold text-2xl text-pink-500">
            {completedCount} / {SELFIE_LEVELS}
          </div>
          <div className="text-text-tertiary font-semibold text-[10px] mt-2 opacity-80 uppercase tracking-widest">
            {isAllLevelsCompleted 
              ? t("onboarding.badgeEarned", { defaultValue: "Rozet Kazanıldı! 🏆" })
              : t("onboarding.stepsToBadge", { count: SELFIE_LEVELS - completedCount, defaultValue: `Rozete ${SELFIE_LEVELS - completedCount} adım kaldı` })}
          </div>
        </div>

        {/* 3. Kart: Kullanıcı Profili (Selfie Yükleme Alanı) */}
        <div 
          onClick={openUploadModal}
          className="glass-card rounded-2xl overflow-hidden flex flex-col border border-border-primary relative group cursor-pointer shadow-sm transition-all hover:shadow-lg hover:border-blue-500/50 hover:scale-[1.02]"
        >
          <div className="flex items-center gap-4 p-6 border-b border-border-primary bg-bg-secondary/50">
            <div className="relative">
              {uploadedSelfieUrl ? (
                <>
                  <img src={uploadedSelfieUrl} alt="User" className="w-20 h-20 rounded-2xl object-cover border-2 border-white dark:border-gray-800 shadow-xl" />
                  <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </>
              ) : (
                <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-border-primary bg-bg-tertiary flex items-center justify-center shadow-xl group-hover:bg-blue-500/10 transition-colors">
                  <Camera className="h-8 w-8 text-text-tertiary group-hover:text-blue-500" />
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-2 border-bg-primary rounded-full" />
            </div>
            
            <div>
              <h3 className="font-bold text-xl leading-tight text-text-primary">
                {user?.name || t("onboarding.adventurer", { defaultValue: "Kullanıcı" })}
              </h3>
            </div>
          </div>

          <div className="p-6 flex-1 flex flex-col justify-center">
            {displayBadges.length > 0 ? (
              <div className="flex flex-col gap-2">
                <span className="text-[10px] text-text-tertiary font-bold uppercase tracking-wider">{t("onboarding.achievements", { defaultValue: "BAŞARILAR" })}</span>
                <BadgesStrip badges={displayBadges} />
              </div>
            ) : (
              <div className="text-center animate-pulse">
                <p className="text-sm font-bold text-blue-500 dark:text-blue-400">
                  {t("onboarding.uploadToBegin", { defaultValue: "Başlamak için fotoğrafınızı yükleyin." })}
                </p>
              </div>
            )}
          </div>
        </div>

      </div>

      <section className="relative min-h-150 py-12">
        
        <div className="flex items-center gap-3 mb-16 relative z-10 pl-4 sm:pl-0">
          <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-inner">
            <Trophy className="text-blue-500 h-6 w-6" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-text-primary tracking-tight">
             {t("onboarding.transformationSteps", { defaultValue: "Dönüşüm Adımları" })}
          </h2>
        </div>

        <SelfieGrid 
          state={cellState} 
          onSelect={(id) => {
            const isUnlocked = cellState[id]?.unlocked;
            try { mixpanel.track("onboarding_level_clicked", { level_id: id, is_locked: !isUnlocked }); } catch (e) {}
            if (!isUnlocked) return;
            openPromptModal(id);
          }} 
          cameraIconSrc={cameraIconSrc} 
        />
      </section>

      {/* Yanlışlıkla modal kapanırsa diye Finali Gör butonu */}
      {isAllLevelsCompleted && (
        <div className="flex justify-center mt-8 animate-in fade-in zoom-in duration-500">
          <Button 
            variant="darkPrimary" 
            size="lg" 
            className="px-10 py-6 text-lg rounded-full shadow-xl shadow-fuchsia-500/20 hover:scale-105 transition-transform gap-2 bg-linear-to-r from-fuchsia-500 to-purple-600 border-0"
            onClick={() => openPromptModal("FINAL")} 
          >
            <Rocket className="h-5 w-5" />
            {t("onboarding.seeFinal", { defaultValue: "Finali Gör" })}
          </Button>
        </div>
      )}

      {footerSlot}

      <SelfieLevelDialog
        resultUrlByLevelId={resultByLevelId}
        completedCount={completedCount}
        creditsRemaining={creditsRemaining}
        creditsTotal={creditsTotal}
      />
      <SelfieUploadModal />
    </div>
  );
}